import React, { useState, useCallback, useEffect } from "react";
import { Clock, Download, Share2, FileText, Search } from "lucide-react";
import Button from "../../../components/common/Button";
import Badge from "../../../components/common/Badge";
import Skeleton from "../../../components/common/Skeleton";
import FormControl from "../../../components/common/FormControl";
import NoDataFound from "../../../components/common/NoDataFound";
import Pagination from "../../../components/common/Pagination";
import apiClient from "../../../services/apiService";
import { statusVariant } from "../../../utils/statusVariant";

interface SopItem {
  id: number;
  process_name: string;
  description: string;
  process_status: string;
  review_cycle_months?: number;
  workflow_status?: string;
  // Maker-checker state — the real approval gate (matches the ApprovalDot).
  workflow_state?: string;
}

const CardSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="action-card action-card--skeleton">
        <div className="action-card__skel-header">
          <Skeleton.Box width={34} height={34} radius={8} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            <Skeleton.Box width="60%" height={12} />
            <Skeleton.Box width="40%" height={10} />
          </div>
          <Skeleton.Pill width={64} height={20} />
        </div>
        <div className="action-card__skel-meta">
          <Skeleton.Box width={80} height={10} />
          <Skeleton.Box width={120} height={10} />
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <Skeleton.Box width="80%" height={32} radius={6} />
          <Skeleton.Box width={32} height={32} radius={6} />
        </div>
      </div>
    ))}
  </>
);

const PAGE_SIZE = 10;

const VaultSopsBody: React.FC = () => {
  const [sops, setSops] = useState<SopItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchSops = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, limit: pageSize };
      if (searchTerm) params.search = searchTerm;
      const res = await apiClient.get("/api/v1/gov/processes", { params });
      setSops(res.data.data || res.data.items || []);
      setTotalRecords(res.data.meta?.total ?? 0);
    } catch {
      setSops([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm]);

  useEffect(() => { fetchSops(); }, [fetchSops]);
  useEffect(() => { setPage(1); }, [searchTerm, pageSize]);

  return (
    <div className="netbody-card flex flex-col h-full">
      <div className="netbody-header">
        <div style={{ marginLeft: "auto" }}>
          <FormControl.Input
            data-testid="INP_vault_sops_search"
            type="text"
            placeholder="Search SOPs…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leadingIcon={<Search size={14} />}
            className="filter-search"
          />
        </div>
      </div>

      <div className="netbody-content">
        <div className="scroll-container">
          {loading ? (
            <div className="action-card-grid">
              <CardSkeleton count={6} />
            </div>
          ) : sops.length === 0 ? (
            <NoDataFound
              title={searchTerm ? "No Matching SOPs" : "No SOPs Found"}
              message={
                searchTerm
                  ? "No SOPs match your search."
                  : "No SOPs have been added yet."
              }
            />
          ) : (
            <div className="action-card-grid">
              {sops.map((sop) => (
                <div key={sop.id} className="action-card">
                  <div className="action-card__header">
                    <div className="action-card__icon">
                      <FileText size={16} />
                    </div>
                    <div className="action-card__title-block">
                      <span className="action-card__title">{sop.process_name}</span>
                      <span className="action-card__subtitle">{sop.description}</span>
                    </div>
                    <div className="action-card__badge">
                      {/* Show maker-checker workflow_state (matches ApprovalDot),
                          not legacy process_status which lies after a reopen. */}
                      <Badge variant={statusVariant(sop.workflow_state)} size="sm">
                        {sop.workflow_state || "draft"}
                      </Badge>
                    </div>
                  </div>

                  {sop.review_cycle_months && (
                    <div className="action-card__meta">
                      <span className="action-card__meta-item">
                        <Clock size={12} />
                        Review every {sop.review_cycle_months} months
                      </span>
                    </div>
                  )}

                  <div className="action-card__footer">
                    <Button
                      data-testid={`BTN_vault_sops_row_${sop.id}_download`}
                      className="btn-primary-outline-none"
                      style={{ flex: 1 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Download size={14} /> Download
                    </Button>
                    <Button
                      data-testid={`BTN_vault_sops_row_${sop.id}_share`}
                     className="btn-primary-outline-none"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Share2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Pagination
            page={page}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            recordLabel="SOPs"
          />
        </div>
      </div>
    </div>
  );
};

export default VaultSopsBody;
